import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import toast from "react-hot-toast";
import {useTranslation} from "react-i18next";
import {AdministrationApi} from "../../api/authority/generated/index.ts";
import useAuthorityConfiguration from "../../api/utilities/useAuthorityConfiguration.js";
import {useErrorTranslation} from "../../api/utilities/translateErrorMessage.js";
import Badge from "../../components/Badge.jsx";
import Button from "../../components/Button.jsx";
import LoadingComponent from "../../components/LoadingComponent.jsx";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import {getTranslatedPlanName} from "../project/Subscription/PlanName.jsx";
import {SortableHeader, TablePagination, useSortableRows} from "./ReportingLab.jsx";

const DEFAULT_PAGE_SIZE = 25;

function paymentSortValue(row, key) {
    if (key === "paidAt") return new Date(row.paidAt ?? row.expectedAt).getTime();
    if (key === "project") return row.projectName;
    if (key === "package") return row.packageCode;
    if (key === "amount") return row.amount;
    if (key === "payment") return row.paymentStatus;
    if (key === "invoice") return row.invoiceStatus;
    return null;
}

function formatMoney(amount, currency) {
    return new Intl.NumberFormat("cs-CZ", {
        style: "currency",
        currency: currency || "CZK",
        maximumFractionDigits: 0,
    }).format(amount ?? 0);
}

function formatDate(value) {
    return value ? new Intl.DateTimeFormat("cs-CZ", {
        dateStyle: "medium",
        timeStyle: "short",
    }).format(new Date(value)) : "—";
}

function SummaryCard({label, value, tone = "slate"}) {
    const tones = {
        slate: "border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800",
        green: "border-emerald-200 bg-emerald-50/60 dark:border-emerald-800 dark:bg-emerald-950/20",
        red: "border-rose-200 bg-rose-50/60 dark:border-rose-800 dark:bg-rose-950/20",
        amber: "border-amber-200 bg-amber-50/60 dark:border-amber-800 dark:bg-amber-950/20",
        violet: "border-violet-200 bg-violet-50/60 dark:border-violet-800 dark:bg-violet-950/20",
    };

    return <div className={`rounded-2xl border p-4 shadow-sm ${tones[tone]}`}>
        <div className="text-sm text-slate-500 dark:text-slate-400">{label}</div>
        <div className="mt-1 text-2xl font-semibold text-slate-900 dark:text-white">{value}</div>
    </div>;
}

function PackageName({code, t}) {
    if (!code) return <span className="text-slate-400">—</span>;
    const translated = getTranslatedPlanName(t, code);
    return <>
        <span className="font-medium text-slate-900 dark:text-white">{translated ?? code}</span>
        {translated && <span className="ml-1 text-xs text-slate-400">({code})</span>}
    </>;
}

function PaymentStatus({status, t}) {
    if (status === "succeeded") return <Badge color="emerald">{t("admin.subscription_payments.status_succeeded", {defaultValue: "Strženo"})}</Badge>;
    if (status === "failed") return <Badge color="rose">{t("admin.subscription_payments.status_failed", {defaultValue: "Neúspěšné"})}</Badge>;
    if (status === "untracked") return <Badge color="slate">{t("admin.subscription_payments.status_untracked", {defaultValue: "Historická platba bez uloženého ID"})}</Badge>;
    if (status === "missing") return <Badge color="rose">{t("admin.subscription_payments.status_missing", {defaultValue: "Platba nevznikla"})}</Badge>;
    if (status === "pending") return <Badge color="yellow">{t("admin.subscription_payments.status_pending", {defaultValue: "Čeká na dokončení"})}</Badge>;
    return <Badge color="yellow">{status || t("admin.subscription_payments.status_unknown", {defaultValue: "Neznámý stav"})}</Badge>;
}

function InvoiceStatus({row, t}) {
    if (row.paymentStatus === "untracked") {
        return <span className="text-slate-400">{t("admin.subscription_payments.invoice_untracked", {defaultValue: "Historická faktura není evidována"})}</span>;
    }
    if (row.paymentStatus !== "succeeded") {
        return <span className="text-slate-400">{t("admin.subscription_payments.invoice_not_expected", {defaultValue: "Čeká na úspěšnou platbu"})}</span>;
    }
    if (row.invoiceStatus === "missing") {
        return <Badge color="rose">{t("admin.subscription_payments.invoice_missing", {defaultValue: "Faktura nenalezena"})}</Badge>;
    }

    return <div className="flex flex-wrap items-center gap-1.5">
        <Badge color={["paid", "generated"].includes(row.invoiceStatus) ? "emerald" : "yellow"}>
            {["paid", "generated"].includes(row.invoiceStatus)
                ? t("admin.subscription_payments.invoice_paid", {defaultValue: "Vygenerována"})
                : row.invoiceStatus}
        </Badge>
        {!row.invoiceMatchIsExact && <Badge color="slate">{t("admin.subscription_payments.match_estimated", {defaultValue: "Spárováno odhadem"})}</Badge>}
    </div>;
}

export default function SubscriptionPayments() {
    const {t} = useTranslation();
    const {et} = useErrorTranslation();
    const configuration = useAuthorityConfiguration();
    const requestDependenciesRef = useRef({configuration, et, t});
    requestDependenciesRef.current = {configuration, et, t};
    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const {configuration: currentConfiguration} = requestDependenciesRef.current;
            setOverview(await new AdministrationApi(currentConfiguration).listAdminSubscriptionPayments());
        } catch (error) {
            const {et: translateError, t: translate} = requestDependenciesRef.current;
            toast.error(await translateError(error) ?? translate("admin.subscription_payments.load_failed", {defaultValue: "Platby se nepodařilo načíst."}));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { void load(); }, [load]);

    const payments = useMemo(() => (overview?.payments ?? []).filter((row) => {
        if (statusFilter !== "all" && row.paymentStatus !== statusFilter) return false;
        const needle = search.trim().toLocaleLowerCase("cs");
        return needle === "" || [
            row.projectName,
            row.packageCode,
            row.stripeChargeId,
            row.stripePaymentIntentId,
            row.stripeInvoiceId,
            row.subscriptionId,
        ].some((value) => String(value ?? "").toLocaleLowerCase("cs").includes(needle));
    }), [overview, search, statusFilter]);
    const {sortedRows: sortedPayments, sort, toggleSort} = useSortableRows(payments, "paidAt", paymentSortValue, "desc");
    const pageCount = Math.max(1, Math.ceil(sortedPayments.length / pageSize));
    const currentPage = Math.min(page, pageCount);
    const visiblePayments = useMemo(
        () => sortedPayments.slice((currentPage - 1) * pageSize, currentPage * pageSize),
        [currentPage, pageSize, sortedPayments],
    );

    useEffect(() => {
        setPage(1);
    }, [search, statusFilter, sort.key, sort.direction, pageSize]);

    return <WideSecureLayout
        title={t("admin.subscription_payments.title", {defaultValue: "Platby"})}
        actions={<Button variant="outline-primary" onClick={load} disabled={loading}>{t("admin.subscription_payments.refresh", {defaultValue: "Obnovit"})}</Button>}
    >
        <div className="space-y-6">
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                <SummaryCard tone="green" label={t("admin.subscription_payments.successful", {defaultValue: "Úspěšně stržené"})} value={overview?.successfulPaymentCount ?? 0}/>
                <SummaryCard tone="red" label={t("admin.subscription_payments.failed", {defaultValue: "Neúspěšné platby"})} value={overview?.failedPaymentCount ?? 0}/>
                <SummaryCard tone="red" label={t("admin.subscription_payments.missing_payments", {defaultValue: "Nezaplacené po pokusu"})} value={overview?.missingPaymentCount ?? 0}/>
                <SummaryCard tone="amber" label={t("admin.subscription_payments.missing_invoices", {defaultValue: "Bez nalezené faktury"})} value={overview?.missingInvoiceCount ?? 0}/>
                <SummaryCard tone="violet" label={t("admin.subscription_payments.upcoming_14_days", {defaultValue: "Nadcházející do 14 dní"})} value={overview?.upcomingPaymentCount ?? 0}/>
            </div>

            <section>
                <h2 className="mb-3 text-lg font-semibold text-slate-900 dark:text-white">{t("admin.subscription_payments.upcoming_title", {defaultValue: "Nadcházející platby v příštích 14 dnech"})}</h2>
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-700">
                            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:bg-slate-900/40">
                                <tr><th className="px-5 py-3">{t("admin.subscription_payments.due_at", {defaultValue: "Splatnost"})}</th><th className="px-5 py-3">{t("admin.subscription_payments.project", {defaultValue: "Projekt"})}</th><th className="px-5 py-3">{t("admin.subscription_payments.package", {defaultValue: "Balíček"})}</th><th className="px-5 py-3">{t("admin.subscription_payments.billing_cycle", {defaultValue: "Fakturace"})}</th><th className="px-5 py-3 text-right">{t("admin.subscription_payments.amount", {defaultValue: "Částka"})}</th></tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700/70">
                                {(overview?.upcomingPayments ?? []).map((row) => <tr key={`${row.projectId}-${row.dueAt}`} className="odd:bg-white even:bg-slate-50/80 dark:odd:bg-slate-800 dark:even:bg-slate-900/30">
                                    <td className="whitespace-nowrap px-5 py-4">{formatDate(row.dueAt)}{row.isTrial && <div className="mt-1"><Badge color="blue">{t("admin.subscription_payments.trial", {defaultValue: "Konec zkoušky"})}</Badge></div>}</td>
                                    <td className="px-5 py-4 font-medium">{row.projectName}</td>
                                    <td className="px-5 py-4"><PackageName code={row.packageCode} t={t}/></td>
                                    <td className="px-5 py-4">{row.billingCycle === "year" ? t("admin.subscription_payments.yearly", {defaultValue: "Roční"}) : t("admin.subscription_payments.monthly", {defaultValue: "Měsíční"})}</td>
                                    <td className="whitespace-nowrap px-5 py-4 text-right font-semibold">{formatMoney(row.amount, row.currency)}</td>
                                </tr>)}
                                {!loading && (overview?.upcomingPayments ?? []).length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-500">{t("admin.subscription_payments.no_upcoming", {defaultValue: "V příštích 14 dnech nejsou naplánované žádné platby."})}</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            <section>
                <div className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t("admin.subscription_payments.history_title", {defaultValue: "Historie plateb"})}</h2>
                    <div className="grid gap-2 sm:grid-cols-[minmax(18rem,28rem)_12rem]">
                        <input className="form-input w-full" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t("admin.subscription_payments.search", {defaultValue: "Projekt, balíček nebo Stripe ID…"})}/>
                        <select className="form-select w-full" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                            <option value="all">{t("admin.subscription_payments.all_statuses", {defaultValue: "Všechny stavy"})}</option>
                            <option value="succeeded">{t("admin.subscription_payments.status_succeeded", {defaultValue: "Strženo"})}</option>
                            <option value="failed">{t("admin.subscription_payments.status_failed", {defaultValue: "Neúspěšné"})}</option>
                            <option value="untracked">{t("admin.subscription_payments.status_untracked", {defaultValue: "Historická platba bez uloženého ID"})}</option>
                            <option value="missing">{t("admin.subscription_payments.status_missing", {defaultValue: "Platba nevznikla"})}</option>
                            <option value="pending">{t("admin.subscription_payments.status_pending", {defaultValue: "Čeká na dokončení"})}</option>
                        </select>
                    </div>
                </div>
                <LoadingComponent loading={loading}>
                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-700">
                                <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:bg-slate-900/40">
                                    <tr>
                                        <SortableHeader className="px-5 py-3" label={t("admin.subscription_payments.paid_at", {defaultValue: "Datum platby / očekávání"})} sortKey="paidAt" sort={sort} onSort={toggleSort}/>
                                        <SortableHeader className="px-5 py-3" label={t("admin.subscription_payments.project", {defaultValue: "Projekt"})} sortKey="project" sort={sort} onSort={toggleSort}/>
                                        <SortableHeader className="px-5 py-3" label={t("admin.subscription_payments.package", {defaultValue: "Balíček"})} sortKey="package" sort={sort} onSort={toggleSort}/>
                                        <SortableHeader className="px-5 py-3" label={t("admin.subscription_payments.amount", {defaultValue: "Částka"})} sortKey="amount" sort={sort} onSort={toggleSort} align="right"/>
                                        <SortableHeader className="px-5 py-3" label={t("admin.subscription_payments.payment", {defaultValue: "Platba"})} sortKey="payment" sort={sort} onSort={toggleSort}/>
                                        <SortableHeader className="px-5 py-3" label={t("admin.subscription_payments.invoice", {defaultValue: "Faktura"})} sortKey="invoice" sort={sort} onSort={toggleSort}/>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-700/70">
                                    {visiblePayments.map((row) => <tr key={`${row.subscriptionId}-${row.stripePaymentIntentId ?? "missing"}`} className="transition-colors odd:bg-white even:bg-slate-50/80 hover:!bg-violet-50 dark:odd:bg-slate-800 dark:even:bg-slate-900/30">
                                        <td className="whitespace-nowrap px-5 py-4">
                                            {formatDate(row.paidAt ?? row.expectedAt)}
                                            {!row.paidAt && <div className="mt-1 text-xs text-rose-600">{t("admin.subscription_payments.expected_at", {defaultValue: "Očekávaný termín"})}</div>}
                                            <div className="mt-1 font-mono text-[11px] text-slate-400">{row.stripePaymentIntentId ?? row.subscriptionId}</div>
                                        </td>
                                        <td className="px-5 py-4">{row.projectName}</td>
                                        <td className="px-5 py-4"><PackageName code={row.packageCode} t={t}/></td>
                                        <td className="whitespace-nowrap px-5 py-4 text-right font-semibold">{formatMoney(row.amount, row.currency)}</td>
                                        <td className="px-5 py-4"><PaymentStatus status={row.paymentStatus} t={t}/>{row.failureReason && <div className="mt-1 max-w-64 text-xs text-rose-600">{row.failureReason}</div>}</td>
                                        <td className="px-5 py-4"><InvoiceStatus row={row} t={t}/>{row.stripeInvoiceId && <div className="mt-1 font-mono text-[11px] text-slate-400">{row.stripeInvoiceId}</div>}</td>
                                    </tr>)}
                                    {!loading && payments.length === 0 && <tr><td colSpan={6} className="px-5 py-12 text-center text-slate-500">{t("admin.subscription_payments.empty", {defaultValue: "Filtrům neodpovídá žádná platba."})}</td></tr>}
                                </tbody>
                            </table>
                        </div>
                        <div className="px-5 pb-4">
                            <TablePagination
                                page={currentPage}
                                pageSize={pageSize}
                                totalItems={sortedPayments.length}
                                onPageChange={setPage}
                                onPageSizeChange={(value) => setPageSize(value)}
                                pageSizeOptions={[10, 25, 50, 100]}
                                t={t}
                            />
                        </div>
                    </div>
                </LoadingComponent>
            </section>
        </div>
    </WideSecureLayout>;
}
